/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint browser:true, unparam:true, todo: true */

"use strict";

var ko = require("knockout");
var R = require("ramda");
var RowSelectionModel = require("slickgrid/plugins/slick.rowselectionmodel");
var SlickEventHandler = require("slickgrid/core").EventHandler;
var SlickGrid = require("slickgrid/grid");

var keyMapUtil = require("../util/keyMap");
var GridViewModel = require("./knockoutGridViewModel");
var GroupSelectionPlugin = require("./slick.groupselectionplugin");
var util = require("./util");

var KeyMap = keyMapUtil.default;

//-------------------------------------------
// Convert a dictionary of string:string keys and values to lower case
function dictionaryToLower(dict) {
    return R.reduce(
        function dictionaryEntryToLower(target, key) {
            target[R.toLower(key)] = R.toLower(dict[key] || "") || null;
            return target;
        },
        {}, // seed - target object
        R.keys(dict) // collection to "reduce"
    );
}


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// GridBinding
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Create a new instance of the grid controller.
function GridController(element, value) {
    this.viewModel = ko.unwrap(value);
    if (!(this.viewModel instanceof GridViewModel)) { throw new Error("valueAccessor object must be a viewModel."); }

    this.columns = [];
    this.element = element;
    this.options = {
        autoHeight: false,
        forceFitColumns: false,
        multiSelect: false
    };

    this._gridHandlers = new SlickEventHandler();
    this._slickGrid = null;

    this._pushGridStateSubscription = this.viewModel._pushGridState.subscribe(this._pushGridStateHandler, this);
    this._pushGridStateHandler(this.viewModel._pushGridState.peek());
}


//-------------------------------------------
// Cleans up the grid controller binding removing
// the state subscription and view-model.
GridController.prototype.dispose = function gridControllerDispose() {
    this._disposeGrid();
    this._pushGridStateSubscription.dispose();

    // Preserve current state to ensure this viewmodel can be re-bound
    // to this (or another) grid with the proper state
    this.viewModel._pushGridState({
        columns: this.columns,
        focusedRow: this.focusedRow,
        options: this.options,
        scrollTop: this.scrollTop,
        selectedIds: this.selectedIds,
        sortCols: this.sortCols
    });
    this.viewModel = null;
};


//-------------------------------------------
// Combines the passed grid options with the
// default set of options.
GridController.prototype._combineDefaultOptionsWith = R.merge({
    disableFocusSinkTab: false,
    disableKeyHandler: true, // prevents slick grid from registering a key handler
    explicitInitialization: true,
    idField: "id",
    multiColumnSort: true,
    multiSelect: false,
    renderColumnFilterIcon: false,
    rowHeight: 30
});

//-------------------------------------------
// Combines the passed grid options with the
// default set of options.
GridController.prototype._combineDefaultShortcutsWith = R.merge({
    "ctrl+a": "selectall",
    "down": "movenext",
    "end": "moveend",
    "enter": "default",
    "home": "movehome",
    "left": "togglegroup",
    "numpad1": "moveend",
    "numpad2": "movenext",
    "numpad3": "pagedown",
    "numpad8": "moveprevious",
    "numpad7": "movehome",
    "numpad9": "pageup",
    "pagedown": "pagedown",
    "pageup": "pageup",
    "right": "togglegroup",
    "space": "select",
    "shift+down": "selectnext",
    "shift+end": "selectend",
    "shift+home": "selecthome",
    "shift+numpad2": "selectnext",
    "shift+numpad8": "selectprevious",
    "shift+tab": "moveprevious",
    "shift+up": "selectprevious",
    "tab": "movenext",
    "up": "moveprevious"
});


//-------------------------------------------
// Disposes slick.grid instance cleaning up
// plug-ins and event handlers initialized by
// `_initGrid`
GridController.prototype._disposeGrid = function gridControllerDisposeGrid() {
    this._gridHandlers.unsubscribeAll();
    if (this._slickGrid !== null) {
        this._slickGrid.destroy();
        this._slickGrid = null;
    }
    if (this._resizeHandler) {
        if (typeof window.removeEventListener === "function") {
            window.removeEventListener("resize", this._resizeHandler, false);
        } else {
            window.detachEvent("onresize", this._resizeHandler);
        }
        this._resizeHandler = null;
    }
    this.element.removeEventListener("keydown", this._keydownHandler);
};


//-------------------------------------------
// Key that is used to store controller
// object as a property of bound HTML element
// (similar technique is used by built-in
// Knockout bindings, they are using
// "__ko__" prefix).
GridController.prototype._elementKey = "__kopli__grid";


//-------------------------------------------
// Prevents rendering checkbox for group totals.
GridController.prototype._initCheckboxFormatter = function gridControllerInitCheckboxFormatter(formatter) {
    return function (row, cell, value, columnDef, dataContext) {
        return (dataContext.__groupTotals) ? "" : formatter.apply(this, arguments);
    };
};


//-------------------------------------------
// Adds the checkbox column if multiSelect is
// enabled otherwise removes it.
GridController.prototype._initColumns = function gridControllerInitColumns() {
    var checkboxColumnId = (this.columns[0] && this.columns[0].id);
    if (this.options.multiSelect) {
        if (checkboxColumnId !== "_checkbox") {
            var checkboxColumn = this._selectionPlugin.getCheckboxColumnDefinition();
            checkboxColumn.formatter = this._initCheckboxFormatter(checkboxColumn.formatter);
            this.columns.unshift(checkboxColumn);
        }
    } else if (checkboxColumnId === "_checkbox") {
        this.columns.shift();
    }
    return this.columns;
};


//-------------------------------------------
// Initializes slick.grid including event handlers,
// plugins and selection model.  Can be disposed
// using `_disposeGrid`.
GridController.prototype._initGrid = function gridControllerInitGrid() {
    this._selectionModel = new RowSelectionModel({ selectActiveRow: !this.options.multiSelect });
    this._selectionPlugin = new GroupSelectionPlugin({ renderColumnFilterIcon: this.options.supportFilterColumn });
    this._slickGrid = new SlickGrid(this.element, this.viewModel._dataView, this._initColumns(this.columns), this.options);

    // NOTE: this is important to register this plugin before row selection one,
    // because we need to not propagate some events in some cases
    this._slickGrid.registerPlugin(this._selectionPlugin);

    // register selection model
    this._slickGrid.setSelectionModel(this._selectionModel);

    if (this.selectedIds !== undefined) { this._selectionPlugin.selectRowsByIds(this.selectedIds); }
    if (this.sortCols !== undefined) { this._slickGrid.setSortColumns(this.sortCols); }

    // initialize slick.grid events
    this._gridHandlers.subscribe(this._slickGrid.onActiveCellChanged, this._onActiveCellChanged.bind(this));
    this._gridHandlers.subscribe(this._slickGrid.onClick, this._onClicked.bind(this));
    var columnsUpdated = this._onColumnsUpdated.bind(this);
    this._gridHandlers.subscribe(this._slickGrid.onColumnsReordered, columnsUpdated);
    this._gridHandlers.subscribe(this._slickGrid.onColumnsResized, columnsUpdated);
    if (this.options.supportItemContext) { this._gridHandlers.subscribe(this._slickGrid.onContextMenu, this._onRightClickedItem.bind(this)); }
    this._gridHandlers.subscribe(this._slickGrid.onDblClick, this._onDoubleClicked.bind(this));
    if (this.options.supportColumnContext) { this._gridHandlers.subscribe(this._slickGrid.onHeaderContextMenu, this._onRightClickedColumnHeader.bind(this)); }
    this._gridHandlers.subscribe(this._slickGrid.onScroll, this._onScroll.bind(this));
    this._gridHandlers.subscribe(this._slickGrid.onSort, this._onSort.bind(this));
    if (this.options.supportFilterColumn) { this._gridHandlers.subscribe(this._selectionPlugin.onColumnFilterClick, this._onColumnFilterClicked.bind(this)); }
    this._gridHandlers.subscribe(this._selectionPlugin.onSelectedIdsChange, this._onSelectionChanged.bind(this));

    this._slickGrid.init();

    // Focus needs to happen after the grid has been initialized
    if (this.focusedRow !== undefined) { this._slickGrid.setActiveCell(this.focusedRow, 0); }

    // Scroll top should override position set by focus/selection
    if (this.scrollTop !== undefined) { this._slickGrid.scrollRowToTop(this.scrollTop / this.options.rowHeight); }

    this._resizeHandler = util.debounce(this._slickGrid.resizeCanvas, 250);
    if (typeof window.addEventListener === "function") {
        window.addEventListener("resize", this._resizeHandler, false);
    } else {
        window.attachEvent("onresize", this._resizeHandler);
    }

    // Attach key handler to binding target
    if (this.keyMap) {
        this._keydownHandler = this._onKeyDown.bind(this);
        this.element.addEventListener("keydown", this._keydownHandler);
    }
};


//-------------------------------------------
// Initialize new grid binding by creating
// a new controller object and storing it as
// a property of the bound element.
//
// This is a "static" method, do not use "this".
GridController.prototype._koInit = function gridControllerKoInit(element, valueAccessor) {
    element[GridController.prototype._elementKey] = new GridController(element, valueAccessor());
};


//-------------------------------------------
// Update value of HTML element bound by
// "grid" binding (this method will be
// called by Knockout when bound observable
// sends a change notification or when
// the observable itself changes).
//
// This is a "static" method, do not use "this".
GridController.prototype._koUpdate = function gridControllerKoUpdate(element, valueAccessor) {
    var controller = GridController.prototype._requireController(element);
    var viewModel = valueAccessor();
    if (controller.viewModel !== viewModel) {
        controller.dispose();
        GridController.prototype._koInit(element, valueAccessor);
    }
};


//-------------------------------------------
// Handles slick.grid onActiveCellChanged.
// Send active row to the view-model.
GridController.prototype._onActiveCellChanged = function gridControllerOnActiveCellChanged(e, args) {
    this.viewModel._applyGridState({ focusedRow: (args.row === undefined ? null : args.row) });
};


//-------------------------------------------
// Handles slick.grid onClicked.
GridController.prototype._onClicked = function gridControllerOnClicked(e, args) {
    if (args) { this.viewModel._applyGridState({ itemClick: args.row, mouseX: e.pageX, mouseY: e.pageY }); }
};


//-------------------------------------------
// Handles the click of the column header cell
// filter icon.
GridController.prototype._onColumnFilterClicked = function gridControllerOnColumnFilterClicked(e, args) {
    if (args && args.column) {
        this.viewModel._applyGridState({ filterColumn: args.column.id, mouseX: e.pageX, mouseY: e.pageY });
    }
};


//-------------------------------------------
// Handles slick.grid onColumnsReordered and
// onColumnsResized events.
// Send columns to the view-model, no need
// to re-render.
GridController.prototype._onColumnsUpdated = function gridControllerOnColumnsUpdated() {
    this.viewModel._applyGridState({ columns: this._slickGrid.getColumns() });
};


//-------------------------------------------
// Handles slick.grid onDoubleClicked event and
// triggers the default action.
GridController.prototype._onDoubleClicked = function gridControllerOnDoubleClicked(e, args) {
    if (args) { this.viewModel._applyGridState({ defaultAction: args.row, mouseX: e.pageX, mouseY: e.pageY }); }
};


//-------------------------------------------
// Handles the slick.grid onKeyDown event and
// triggers the specified action.
// A non-truthy action will disable the action.
GridController.prototype._onKeyDown = function gridControllerOnKeyDown(e) {
    var keyMap = this.keyMap;
    var hash = keyMapUtil.calculateHash(e);
    var action = keyMap.lookup[hash];

    if (action) {
        this._performAction(action);
        // stop event bubbling
        e.stopImmediatePropagation();
        // prevent the browser's default action  (ie: select all)
        e.preventDefault();
    }
};


//-------------------------------------------
// Handles slick.grid column header right clicked.
GridController.prototype._onRightClickedColumnHeader = function gridControllerOnRightClickedColumnHeader(e, args) {
    if (args && args.column) {
        this.viewModel._applyGridState({ columnContext: args.column.id, mouseX: e.pageX, mouseY: e.pageY });
    }
    e.stopImmediatePropagation();
    e.preventDefault();
};


//-------------------------------------------
// Handles slick.grid item right clicked.
GridController.prototype._onRightClickedItem = function gridControllerOnRightClickedItem(e, args) {
    var cell = args.grid.getCellFromEvent(e);
    if (args.grid.canCellBeSelected(cell.row, cell.cell)) {
        this.viewModel._applyGridState({ itemContext: cell.row, mouseX: e.pageX, mouseY: e.pageY });
    }
    e.stopImmediatePropagation();
    e.preventDefault();
};


//-------------------------------------------
// Handles slick.grid onScroll event.  Send
// scrollTop to viewModel.
GridController.prototype._onScroll = function gridControllerOnScroll(e, args) {
    this.viewModel._applyGridState({ scrollTop: args.scrollTop });
};


//-------------------------------------------
// Handles slick.grid onSort event.  Sends
// sorted columns to viewModel.
GridController.prototype._onSort = function gridControllerOnSort(e, args) {
    this.viewModel._applyGridState({ sortCols: args.sortCols });
};


//-------------------------------------------
// Handles slick.grid onSort event.  Sends
// sorted columns to viewModel.
GridController.prototype._onSelectionChanged = function gridControllerOnSelectionChanged(e, args) {
    this.viewModel._applyGridState({ selectedIds: args.ids });
};


//-------------------------------------------
// Performs a grid action by name
GridController.prototype._performAction = function gridControllerPerformAction(action) {
    var item, cell, viewport, diff, row;
    if (!action) { return; }

    cell = this._slickGrid.getActiveCell();

    switch (action.toLowerCase()) {
    case "collapseallgroups":
        this.viewModel._dataView.collapseAllGroups(null);
        break;
    case "default":
        this.viewModel._applyGridState({ defaultAction: cell.row });
        break;
    case "expandallgroups":
        this.viewModel._dataView.expandAllGroups(null);
        break;
    case "moveend":
        this._selectionPlugin.navigateHomeOrEnd(false, false, this._slickGrid);
        break;
    case "movehome":
        this._selectionPlugin.navigateHomeOrEnd(true, false, this._slickGrid);
        break;
    case "movenext":
        this._slickGrid.navigateDown();
        break;
    case "moveprevious":
        this._slickGrid.navigateUp();
        break;
    case "pagedown":
        viewport = this._slickGrid.getViewport();
        diff = viewport.bottom - viewport.top - 2;
        row = cell.row + diff;
        row = (row < this._slickGrid.getDataLength()) ? row : this._slickGrid.getDataLength() - 1;
        this._slickGrid.gotoCell(this._selectionPlugin.getClosestFocusableRow(this._slickGrid, row, -1), 0);
        if (row === this._slickGrid.getDataLength() - 1) {
            this._slickGrid.scrollRowToTop(row);
        }
        break;
    case "pageup":
        viewport = this._slickGrid.getViewport();
        diff = viewport.bottom - viewport.top - 2;
        row = cell.row - diff;
        row = (row >= 0) ? row : 0;
        this._slickGrid.gotoCell(this._selectionPlugin.getClosestFocusableRow(this._slickGrid, row, 1), 0);
        break;
    case "select":
        if (this.options.multiSelect) {
            this._selectionPlugin.toggleRowSelection(cell.row);
        }
        break;
    case "selectall":
        if (this.options.multiSelect) {
            this._selectionPlugin.selectAll(true);
        }
        break;
    case "selectend":
        this._selectionPlugin.navigateHomeOrEnd(false, true, this._slickGrid);
        break;
    case "selecthome":
        this._selectionPlugin.navigateHomeOrEnd(true, true, this._slickGrid);
        break;
    case "selectnext":
        this._selectionPlugin.navigateAndSelectUpOrDown(true, this._slickGrid);
        break;
    case "selectnone":
        if (this.options.multiSelect) {
            this._selectionPlugin.selectAll(false);
        }
        break;
    case "selectprevious":
        this._selectionPlugin.navigateAndSelectUpOrDown(false, this._slickGrid);
        break;
    case "togglegroup":
        if (cell) {
            item = this._slickGrid.getDataItem(cell.row);
            if (item.__group) {
                this._selectionPlugin.toggleGroup(item, this._slickGrid, this);
            }
        }
        break;
    default:
        throw new Error("Unrecognized action: " + action);
    }
};



//-------------------------------------------
// Subscription handler for `_pushGridState`
// observable that notifies the grid binding
// that the state of the grid has changed.
GridController.prototype._pushGridStateHandler = function gridControllerPushGridStateHandler(value) {
    var isDisabled = (value.options !== undefined
        ? value.options.disabled
        : this.options.disabled === undefined ? true : this.options.disabled);
    var multiSelectChanged = (value.options !== undefined && value.options.multiSelect !== this.options.multiSelect);

    // update internal state
    if (value.options !== undefined) {
        this.options = this._combineDefaultOptionsWith(value.options);
        if (value.options.shortcutActions === null) {
            this.keyMap = null;
        } else {
            var shortcutActions = this._combineDefaultShortcutsWith(dictionaryToLower(value.options.shortcutActions));

            this.keyMap = new KeyMap(shortcutActions);
            this.keyMap.createLookup();
            this.options.disableFocusSinkTab = this.keyMap.lookup[keyMapUtil.codes.tab] === null;
        }
    }

    if (value.columns !== undefined) { this.columns = value.columns; }
    if (value.focusedRow !== undefined) { this.focusedRow = value.focusedRow; }
    if (value.scrollTop !== undefined) { this.scrollTop = value.scrollTop; }
    if (value.selectedIds !== undefined) { this.selectedIds = value.selectedIds; }
    if (value.sortCols !== undefined) { this.sortCols = value.sortCols; }

    // destroy existing grid if it exists
    if (isDisabled) {
        this._disposeGrid();
        this.options.disabled = true;

    // create new
    } else if (this._slickGrid === null) {
        this._initGrid();

    // re-initialize if multi-select has changed.
    } else if (multiSelectChanged) {
        this._disposeGrid();
        this._initGrid();

    // update existing
    } else {
        if (value.columns !== undefined) { this._slickGrid.setColumns(this._initColumns(this.columns)); }
        if (value.focusedRow !== undefined) { this._slickGrid.setActiveCell(this.focusedRow, 0); }
        if (value.options !== undefined) { this._slickGrid.setOptions(this.options); }
        if (value.resize !== undefined) { this._slickGrid.resizeCanvas(); }
        if (value.invalidate !== undefined) { this._slickGrid.invalidate(); }
        if (value.scrollTop !== undefined) { this._slickGrid.scrollRowToTop(this.scrollTop / this.options.rowHeight); }
        if (value.selectedIds !== undefined) { this._selectionPlugin.selectRowsByIds(this.selectedIds); }
        if (value.sortCols !== undefined) { this._slickGrid.setSortColumns(this.sortCols); }
        if (value.action !== undefined) { this._performAction(value.action); }
    }

};


//-------------------------------------------
// Get controller associated with the specified
// element. Throws an error if the specified
// element is not associated with a controller.
//
// This is a "static" method, do not use "this".
GridController.prototype._requireController = function gridControllerRequireController(element) {
    var controller = (element && element[GridController.prototype._elementKey]);
    if (!controller) { throw new Error("Target element has not been bound using \"grid\" binding"); }
    return controller;
};


module.exports = ko.bindingHandlers.grid = {
    init: GridController.prototype._koInit,
    update: GridController.prototype._koUpdate
};




//////////////////
// WEBPACK FOOTER
// ./~/pli/ko-grid/knockoutGridBinding.js
// module id = 408
// module chunks = 49